import { GrowthBookSSRData, useGrowthBookSSR } from '@growthbook/growthbook-react'
import { IconProvider } from '@hg/stylizator/components'
import * as React from 'react'
import { CookiesProvider } from 'react-cookie'
import { IWPGBlock } from 'react-gutenberg'
import { ThemeProvider } from 'styled-components'

import ClientOnly from './components/ClientOnly'
import GBProvider from './helpers/GrowthbookProvider'
import theme from './styles/theme'

import LinkDecorator from 'app/helpers/LinkDecorator'
import { IResponseReusableBlock, TPageData, TPageType } from 'app/service/api/theme'
import GlobalStyles from 'app/styles/global'

const StylizatorGlobalStyles = React.lazy(() => import('./styles/StylizatorGlobalStyles'))
const CookieConsent = React.lazy(() => import('./components/CookieConsent'))

const pages: Record<
  TPageType,
  React.ComponentType<{ pageData: TPageData<TPageType>; helloBarBlockData?: IResponseReusableBlock }>
> = {
  page: React.lazy(() => import('app/pages/Page')),
  post: React.lazy(() => import('app/pages/PostPage')),
  'post-home': React.lazy(() => import('app/pages/PostHomePage')),
  category: React.lazy(() => import('app/pages/CategoryPage')),
  search: React.lazy(() => import('app/pages/SearchPage')),
  carebuzz: React.lazy(() => import('app/pages/CarebuzzPage')),
  swarmbytes: React.lazy(() => import('app/pages/SwarmbytesPage')),
  'honeygain-sdk': React.lazy(() => import('app/pages/HoneygainSdkPage')),
  'honeygain-clean': React.lazy(() => import('app/pages/HoneygainCleanPage')),
  'honeygain-referral-me': React.lazy(() => import('app/pages/HoneygainReferralMePage')),
  'honeygain-referral-io': React.lazy(() => import('app/pages/HoneygainReferralIoPage')),
}

interface IProps {
  themeData: any
  pageData: any
  pageType: TPageType
  locale: string
  growthbookData: GrowthBookSSRData
}

const getHelloBarBlockData = (blocks: (IWPGBlock & { id: string })[]) => {
  const block = blocks.find((item) => item?.blockName === 'honeygain-components/hello-bar')

  if (!block) {
    return undefined
  }

  return {
    name: block.blockName ?? '',
    attrs: block.attrs,
    id: block.id,
    innerBlocks: block.innerBlocks,
  }
}

const RootApp: React.FC<IProps> = ({ pageData, pageType, growthbookData, themeData }) => {
  const PageComponent = pages?.[pageType]

  useGrowthBookSSR(growthbookData)

  if (!PageComponent) {
    throw new Error(`Component for page type "${pageType}" does not exist`)
  }

  const blocks: (IWPGBlock & { id: string })[] = themeData.blocks
  const helloBarBlockData = getHelloBarBlockData(blocks)

  return (
    <GBProvider growthbookAttributeId={growthbookData.attributes.id}>
      <ThemeProvider theme={theme}>
        <IconProvider spriteHref="/build/sprite.svg">
          <StylizatorGlobalStyles />
          <GlobalStyles />
          <PageComponent pageData={pageData} helloBarBlockData={helloBarBlockData} />
          <ClientOnly>
            <CookiesProvider>
              <CookieConsent />
            </CookiesProvider>
          </ClientOnly>
        </IconProvider>
      </ThemeProvider>
      <LinkDecorator />
    </GBProvider>
  )
}

export default RootApp
